import { useEffect, useState } from "react";

/**
 * IosTapTracer — on-screen event tracer for real-device debugging.
 *
 * Activated by appending `?debug=tap` to any URL. Renders a small
 * fixed-position log at the top-right of the viewport that captures
 * every `pointerdown` / `touchstart` / `click` at the document level
 * (capture phase) so we can see exactly which DOM element the finger
 * hit — including whether an invisible overlay is intercepting taps.
 *
 * Reports for each event:
 *   • event type (pointerdown / touchstart / click)
 *   • (x, y) coordinates in the viewport
 *   • the target element's data-testid (if any), tag, class prefix
 *   • whether propagation was stopped
 *
 * No PII captured. No effect when the query flag is absent. Remove
 * after the iOS interaction debug pass concludes.
 */
export default function IosTapTracer() {
  const [enabled] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("debug") === "tap";
    } catch { return false; }
  });
  const [events, setEvents] = useState([]);
  // Snapshot every payment chip's bounding rect + computed style so
  // we can see on the real device WHERE each chip is and whether it
  // is hit-testable. Runs on mount and after every render (the row
  // wraps once the parent lays out).
  const [chipSnapshot, setChipSnapshot] = useState([]);

  useEffect(() => {
    if (!enabled) return;
    const capture = () => {
      const ids = ["req-pay-cash","req-pay-wallet","req-pay-card","req-pay-applepay","req-pay-googlepay","req-pay-paypal"];
      const out = ids.map((id) => {
        const el = document.querySelector(`[data-testid="${id}"]`);
        if (!el) return { id, exists: false };
        const r = el.getBoundingClientRect();
        const cs = window.getComputedStyle(el);
        // Element at the center point — reveals overlays.
        const cx = Math.round(r.left + r.width / 2);
        const cy = Math.round(r.top + r.height / 2);
        const topEl = document.elementFromPoint(cx, cy);
        const topTid = topEl && topEl.closest && topEl.closest("[data-testid]");
        return {
          id, exists: true,
          x: Math.round(r.left), y: Math.round(r.top),
          w: Math.round(r.width), h: Math.round(r.height),
          in_vp: r.top >= 0 && r.bottom <= window.innerHeight && r.right <= window.innerWidth,
          pe: cs.pointerEvents, ta: cs.touchAction, op: cs.opacity, dis: el.disabled,
          top_at_center: topTid ? topTid.getAttribute("data-testid") : (topEl?.tagName?.toLowerCase() || "?"),
        };
      });
      setChipSnapshot(out);
    };
    // Capture once + on each resize (row wrap etc.)
    setTimeout(capture, 300);
    setTimeout(capture, 1500);
    window.addEventListener("resize", capture);

    const record = (kind) => (e) => {
      try {
        const t = e.target;
        const touch = (e.touches && e.touches[0]) || null;
        const x = Math.round((touch ? touch.clientX : e.clientX) || 0);
        const y = Math.round((touch ? touch.clientY : e.clientY) || 0);
        const chain = [];
        let n = t; let hops = 0;
        while (n && hops < 4) {
          const tid = n.getAttribute && n.getAttribute("data-testid");
          const tag = (n.tagName || "").toLowerCase();
          const cls = ((n.className || "") + "").split(" ").slice(0, 3).join(".");
          chain.push({ tag, tid, cls });
          n = n.parentElement; hops += 1;
        }
        const entry = { kind, x, y, chain, ts: Date.now() };
        setEvents((prev) => [entry, ...prev].slice(0, 12));
      } catch (err) { /* ignore */ }
    };
    const opts = { capture: true, passive: true };
    const h1 = record("pointerdown");
    const h2 = record("touchstart");
    const h3 = record("click");
    document.addEventListener("pointerdown", h1, opts);
    document.addEventListener("touchstart",  h2, opts);
    document.addEventListener("click",       h3, opts);
    return () => {
      window.removeEventListener("resize", capture);
      document.removeEventListener("pointerdown", h1, opts);
      document.removeEventListener("touchstart",  h2, opts);
      document.removeEventListener("click",       h3, opts);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      data-testid="ios-tap-tracer"
      style={{
        position: "fixed", top: 6, right: 6, zIndex: 100000,
        maxWidth: 320, maxHeight: "50vh", overflow: "auto",
        background: "rgba(0,0,0,0.9)", color: "#00E5FF",
        font: "10px/1.25 -apple-system, monospace",
        padding: 8, borderRadius: 8, border: "1px solid #00E5FF",
        pointerEvents: "none",
      }}
    >
      <div style={{ color: "#FFF", fontWeight: "bold", marginBottom: 4 }}>
        Tracer · vp {window.innerWidth}×{window.innerHeight}
      </div>

      <div style={{ color: "#FFC800", fontWeight: "bold", marginTop: 2 }}>Chip snapshot</div>
      {chipSnapshot.length === 0 && <div style={{ color: "#FFF9" }}>waiting…</div>}
      {chipSnapshot.map((c, i) => (
        <div key={i} style={{ borderTop: "1px solid #FFF2", padding: "2px 0" }}>
          <div><b>{c.id}</b> {c.exists ? "" : "MISSING"}</div>
          {c.exists && (
            <>
              <div>({c.x},{c.y}) {c.w}×{c.h} inVP={String(c.in_vp)}</div>
              <div>pe={c.pe} ta={c.ta} op={c.op} dis={String(c.dis)}</div>
              <div style={{ color: c.top_at_center === c.id ? "#00FF88" : "#FF5555" }}>
                topAtCenter=<b>{c.top_at_center}</b>
              </div>
            </>
          )}
        </div>
      ))}

      <div style={{ color: "#FFC800", fontWeight: "bold", marginTop: 6 }}>Events ({events.length})</div>
      {events.length === 0 && <div style={{ color: "#FFF9" }}>tap anywhere…</div>}
      {events.map((e, i) => (
        <div key={i} style={{ borderTop: "1px solid #FFF2", padding: "2px 0" }}>
          <div><b style={{ color: "#FFC800" }}>{e.kind}</b> ({e.x},{e.y})</div>
          {e.chain.slice(0, 3).map((c, j) => (
            <div key={j} style={{ paddingLeft: j * 6 }}>
              {c.tid ? <span style={{ color: "#00E5FF" }}>[{c.tid}]</span>
                     : <span style={{ color: "#FFF9" }}>{c.tag}</span>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
